"use client";

import Link from "next/link";
import { Fragment, useMemo, useState } from "react";

import { fmtIntCA } from "@/lib/format";

import { ClassificationBadge } from "./ClassificationBadge";
import { PerLineVarianceView } from "./PerLineVarianceView";
import {
  filterRows,
  sortRows,
  totalsFor,
  type LineFilter,
  type PerLineYear,
  type SortDir,
  type SortKey,
  type VarianceRow,
} from "../_lib/table";

type Props = {
  rows: VarianceRow[];
  year: number;
  /** Full multi-year series per canonical-id (FR-010). */
  series: Record<string, PerLineYear[]>;
  /** Canonical-ids eligible for per-line drilldown (top-N by |variance|,
   *  SC-006). Rows outside this set show no expander; their long-tail
   *  multi-year data is still reachable via the artefact download (US6). */
  drilldownIds: string[];
};

function fmtThousands(v: number | null): string {
  if (v === null) return "—";
  const sign = v < 0 ? "−" : "";
  return `${sign}$${fmtIntCA(Math.abs(Math.round(v)))}`;
}

function fmtPct(v: number | null): string {
  if (v === null) return "n/a";
  return `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(1)}%`;
}

const COLS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: "label", label: "Line", numeric: false },
  { key: "budget", label: "Budget", numeric: true },
  { key: "actual", label: "Actual", numeric: true },
  { key: "variance_absolute", label: "Variance", numeric: true },
  { key: "variance_percent", label: "Variance %", numeric: true },
];

export function VarianceTable({ rows, year, series, drilldownIds }: Props) {
  const [filter, setFilter] = useState<LineFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("variance_absolute");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [expanded, setExpanded] = useState<string | null>(null);
  const canDrill = useMemo(() => new Set(drilldownIds), [drilldownIds]);

  const view = useMemo(
    () => sortRows(filterRows(rows, filter), sortKey, sortDir),
    [rows, filter, sortKey, sortDir],
  );
  const totals = useMemo(() => totalsFor(view), [view]);

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "label" ? "asc" : "desc");
    }
  }

  return (
    <section
      className="cs-paper py-12 cs-rule"
      aria-labelledby="variance-table-h"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 flex flex-col gap-5">
        <header className="flex flex-col gap-2">
          <p className="cs-mono text-ink-3 text-xs uppercase tracking-wider">
            Consolidated statement of operations · {year}
          </p>
          <h2 id="variance-table-h" className="cs-display text-2xl sm:text-3xl">
            Every line: budgeted vs actual
          </h2>
          <p className="text-ink-2 cs-prose text-sm leading-relaxed">
            All figures in thousands of CAD, read from the {year} Annual
            Financial Report&rsquo;s audited Consolidated Statement of
            Operations (the AFR budget-comparison column is the
            denominator). Sort any column; filter to revenue or expense —
            the totals row tracks the filtered set. The classification is
            editorial; hover or tap it for the rationale.
          </p>
        </header>

        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Filter lines"
        >
          {(
            [
              ["all", "All lines"],
              ["revenue", "Revenue only"],
              ["expense", "Expense only"],
            ] as [LineFilter, string][]
          ).map(([f, lbl]) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={`cs-mono rounded border px-2.5 py-1 text-[12px] uppercase tracking-wider transition-colors ${
                filter === f
                  ? "border-signal/40 bg-signal/10 text-signal"
                  : "border-rule text-ink-3 hover:text-ink"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="text-left">
                {COLS.map((c) => {
                  const active = c.key === sortKey;
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      aria-sort={
                        active
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                      className={`pb-2 ${c.numeric ? "text-right" : "text-left"}`}
                    >
                      <button
                        type="button"
                        onClick={() => onSort(c.key)}
                        className="cs-mono text-[12px] uppercase tracking-wider text-ink-3 hover:text-ink"
                      >
                        {c.label}
                        {active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                      </button>
                    </th>
                  );
                })}
                <th
                  scope="col"
                  className="pb-2 text-left cs-mono text-[12px] uppercase tracking-wider text-ink-3"
                >
                  Class
                </th>
                <th
                  scope="col"
                  className="pb-2 text-right cs-mono text-[12px] uppercase tracking-wider text-ink-3"
                >
                  Source
                </th>
              </tr>
            </thead>
            <tbody>
              {view.map((r) => {
                const drillable = canDrill.has(r.canonical_id);
                const isOpen = expanded === r.canonical_id;
                return (
                <Fragment key={r.canonical_id}>
                <tr
                  id={`line-${r.canonical_id}`}
                  className="border-t border-rule scroll-mt-24 align-top"
                >
                  <td className="py-2 pr-3">
                    <span className="flex items-baseline gap-1.5">
                      {drillable ? (
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          // Only reference the controlled element while it
                          // exists in the DOM — a dangling IDREF is an a11y
                          // defect when collapsed.
                          aria-controls={
                            isOpen ? `drill-${r.canonical_id}` : undefined
                          }
                          aria-label={`${
                            isOpen ? "Hide" : "Show"
                          } multi-year budget vs actual for ${r.label}`}
                          onClick={() =>
                            setExpanded(isOpen ? null : r.canonical_id)
                          }
                          className="cs-mono text-[12px] text-ink-3 hover:text-signal transition-colors"
                        >
                          <span aria-hidden>{isOpen ? "▾" : "▸"}</span>
                        </button>
                      ) : (
                        <span
                          aria-hidden
                          className="inline-block w-[11px]"
                        />
                      )}
                      {r.cross_link ? (
                        <Link
                          href={r.cross_link}
                          className="cs-rule-underline text-ink hover:text-signal transition-colors"
                        >
                          {r.label}
                        </Link>
                      ) : (
                        <span className="text-ink">{r.label}</span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {r.budget_not_set ? (
                      <span className="text-ink-3" title="Not budgeted">
                        not budgeted
                      </span>
                    ) : (
                      fmtThousands(r.budget_thousands)
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {fmtThousands(r.actual_thousands)}
                  </td>
                  <td className="py-2 text-right">
                    {fmtThousands(r.variance_absolute_thousands)}
                  </td>
                  <td
                    className={`py-2 text-right ${
                      r.variance_percent === null
                        ? "text-ink-3"
                        : r.variance_percent >= 0
                          ? "text-signal"
                          : "text-amber-700"
                    }`}
                  >
                    {fmtPct(r.variance_percent)}
                  </td>
                  <td className="py-2 pl-3">
                    <ClassificationBadge
                      classification={r.classification}
                      rationale={r.classification_rationale}
                    />
                  </td>
                  <td className="py-2 pl-3 text-right">
                    {r.source_pdf_url && r.actual_source_page ? (
                      <a
                        href={`${r.source_pdf_url}#page=${r.actual_source_page}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cs-mono text-[12px] text-ink-3 cs-rule-underline hover:text-signal transition-colors"
                      >
                        AFR p.{r.actual_source_page}
                      </a>
                    ) : (
                      <span className="cs-mono text-[12px] text-ink-3">—</span>
                    )}
                  </td>
                </tr>
                {drillable && isOpen ? (
                  <tr className="border-t border-rule/50 bg-ink/[0.015]">
                    <td colSpan={7} className="px-3 py-4">
                      <div id={`drill-${r.canonical_id}`}>
                        <p className="cs-mono text-[12px] uppercase tracking-wider text-ink-3 mb-2">
                          {r.label} · budget vs actual, multi-year
                        </p>
                        <PerLineVarianceView
                          label={r.label}
                          series={series[r.canonical_id] ?? []}
                          ariaLabel={`${r.label} budget vs actual by year`}
                        />
                      </div>
                    </td>
                  </tr>
                ) : null}
                </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-ink/30 font-medium">
                <td className="py-2 pr-3 cs-mono text-[12px] uppercase tracking-wider text-ink-2">
                  Total · {totals.count}{" "}
                  {filter === "all" ? "lines" : `${filter} lines`}
                </td>
                <td className="py-2 text-right">
                  {fmtThousands(totals.budget_thousands)}
                </td>
                <td className="py-2 text-right">
                  {fmtThousands(totals.actual_thousands)}
                </td>
                <td className="py-2 text-right">
                  {fmtThousands(totals.variance_absolute_thousands)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
}
